import type { EChartsOption } from "echarts";
import {
  BAR_BORDER_RADIUS_VERTICAL,
  buildAxisLabelStyle,
  buildLegendTextStyle,
  buildTooltipStyle,
  buildTooltipValueStyle,
  chartGridLineColor,
  CONTAIN_AXIS_LABELS,
  CURRENCY_FORMATTER,
  formatImpressionAxis,
  IMPRESSION_FORMATTER,
  LEGEND_BASE,
} from "@/pages/CampaignOverviewPage/charts/chartHelpers";
import {
  getChartPalette,
  type ChartPalette,
} from "@/pages/CampaignOverviewPage/charts/chartPalette";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";

export type ChartType = "bar" | "line" | "area";

const CHART_GRID = { bottom: 56, left: 56, right: 56, top: 40 } as const;
const LINE_STROKE_WIDTH = 2.5;

const IMPRESSIONS_LABEL = TEXT.campaignOverview.chart.impressionsSeriesLabel;
const SPEND_LABEL = TEXT.campaignOverview.chart.spendSeriesLabel;
const DATA_SERIES_LABELS: string[] = [IMPRESSIONS_LABEL, SPEND_LABEL];

const sortedByDate = (rows: PerformanceReportRow[]) =>
  [...rows].sort((rowA, rowB) =>
    (rowA.agg_timestamp ?? "").localeCompare(rowB.agg_timestamp ?? "")
  );

// agg_timestamp arrives as a raw ISO 8601 instant (e.g. "2026-07-09T00:00:00Z")
// — every value here is UTC midnight, so formatting must pin to UTC too,
// otherwise a viewer behind UTC would see the day before the one aggregated.
const AXIS_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

const formatAxisDate = (timestamp: string): string => {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime())
    ? timestamp
    : AXIS_DATE_FORMATTER.format(date);
};

const formatSpendAxis = (value: number | string): string => {
  const num = Number(value);
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${Math.round(num / 1_000)}K`;
  return `$${num.toFixed(0)}`;
};

const AXIS_SPLIT_NUMBER = 5;
// Interval multipliers that read cleanly on an axis (× 10^n). Includes 3 and 4
// so a 15M peak gets a 20M axis rather than jumping to 25M.
const NICE_STEP_FACTORS = [1, 2, 2.5, 3, 4, 5, 10];

// Rounds an axis maximum up so `max / splitNumber` is a "nice" interval. Both
// y-axes get a fixed max with the same split count, which makes their gridlines
// coincide and lets the spend line be projected onto the impressions axis to
// draw the band between the two lines.
export const niceAxisMax = (
  maxValue: number,
  splitNumber = AXIS_SPLIT_NUMBER
): number => {
  if (maxValue <= 0) return splitNumber;
  const rawStep = maxValue / splitNumber;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const factor =
    NICE_STEP_FACTORS.find((candidate) => candidate * magnitude >= rawStep) ??
    10;
  return factor * magnitude * splitNumber;
};

type MarkLineData = { coord: [number, number] };

const BAND_BASE_SERIES = "band-base";
const BAND_FILL_SERIES = "band-fill";

const DOT_BORDER_WIDTH = 1.5;
const DOT_SIZE = 8;
// Past this many x ticks the guide lines are as dense as the data itself, so
// they are dropped rather than drawing and re-laying-out one per day.
const MAX_GUIDE_LINE_POINTS = 90;

// Dot outlines use the card color, so they read as a gap in either theme.
const buildSolidLineSeries = (color: string, outline: string) => ({
  itemStyle: { borderColor: outline, borderWidth: DOT_BORDER_WIDTH, color },
  lineStyle: { color, width: LINE_STROKE_WIDTH },
  smooth: true,
  symbol: "circle",
  symbolSize: DOT_SIZE,
});

// Spend line projected onto the impressions axis. Valid because both y-axes
// share a fixed max and split count, so equal fractions of each axis land at
// the same pixel height.
const projectSpend = (
  spend: number[],
  impressionsMax: number,
  spendMax: number
): number[] => {
  const scale = spendMax > 0 ? impressionsMax / spendMax : 0;
  return spend.map((value) => value * scale);
};

// Purple band sits only between the impressions line and the spend line, and
// stays empty where spend plots above impressions. Built as two stacked,
// invisible-base series on the impressions axis: a transparent base up to the
// lower of the two lines, then the visible gap on top of it.
const buildBandSeries = (
  impressions: number[],
  projectedSpend: number[],
  gradient: ChartPalette["bandGradient"]
) => {
  const { base, gap } = impressions.reduce(
    (acc, value, index) => {
      const lower = Math.min(value, projectedSpend[index]);
      acc.base.push(lower);
      acc.gap.push(value - lower);
      return acc;
    },
    { base: [] as number[], gap: [] as number[] }
  );
  const shared = {
    emphasis: { disabled: true },
    silent: true,
    smooth: true,
    stack: "band",
    symbol: "none",
    type: "line" as const,
    yAxisIndex: 0,
  };
  return [
    {
      ...shared,
      data: base,
      lineStyle: { opacity: 0 },
      name: BAND_BASE_SERIES,
    },
    {
      ...shared,
      areaStyle: { color: gradient },
      data: gap,
      lineStyle: { opacity: 0 },
      name: BAND_FILL_SERIES,
    },
  ];
};

// Guide from each impressions point down to the baseline, in the card color.
const buildGuideLines = (impressions: number[], color: string) => ({
  animation: false,
  data: impressions.map((value, index): [MarkLineData, MarkLineData] => [
    { coord: [index, value] },
    { coord: [index, 0] },
  ]),
  lineStyle: {
    color,
    type: "solid" as const,
    width: 1,
  },
  silent: true,
  symbol: "none",
});

// Bar, Line, and Area share the same two-series shape — impressions (plum) on
// the left axis, total_spend (orange-red) always a line on the right axis.
const buildTimeSeriesOption = (
  rows: PerformanceReportRow[],
  primaryType: "bar" | "line",
  areaStyle: boolean,
  isDark: boolean
): EChartsOption => {
  const axisLabelStyle = buildAxisLabelStyle(isDark);
  const legendTextStyle = buildLegendTextStyle(isDark);
  const palette = getChartPalette(isDark);
  const valueStyle = buildTooltipValueStyle(isDark);
  const gridLineColor = chartGridLineColor(isDark);

  const sorted = sortedByDate(rows);
  const { dates, impressions, spend } = sorted.reduce(
    (acc, row) => {
      acc.dates.push(formatAxisDate(row.agg_timestamp ?? ""));
      acc.impressions.push(row.impressions ?? 0);
      acc.spend.push(row.total_spend ?? 0);
      return acc;
    },
    {
      dates: [] as string[],
      impressions: [] as number[],
      spend: [] as number[],
    }
  );

  const impressionsMax = niceAxisMax(Math.max(0, ...impressions));
  const spendMax = niceAxisMax(Math.max(0, ...spend));
  const bandSeries = areaStyle
    ? buildBandSeries(
        impressions,
        projectSpend(spend, impressionsMax, spendMax),
        palette.bandGradient
      )
    : [];

  return {
    grid: { ...CHART_GRID, ...CONTAIN_AXIS_LABELS },
    // Legend swatches are always flat, even when the series draws a gradient.
    legend: {
      ...LEGEND_BASE,
      data: [
        { itemStyle: { color: palette.primary }, name: IMPRESSIONS_LABEL },
        { itemStyle: { color: palette.orange }, name: SPEND_LABEL },
      ],
      textStyle: legendTextStyle,
    },
    series: [
      primaryType === "bar"
        ? {
            data: impressions,
            itemStyle: {
              borderRadius: BAR_BORDER_RADIUS_VERTICAL,
              color: palette.barGradientV,
            },
            name: IMPRESSIONS_LABEL,
            type: "bar" as const,
            yAxisIndex: 0,
          }
        : {
            ...buildSolidLineSeries(palette.primary, palette.card),
            data: impressions,
            markLine:
              areaStyle && impressions.length <= MAX_GUIDE_LINE_POINTS
                ? buildGuideLines(impressions, palette.guideLine)
                : undefined,
            name: IMPRESSIONS_LABEL,
            type: "line" as const,
            yAxisIndex: 0,
            z: 3,
          },
      {
        ...buildSolidLineSeries(palette.orange, palette.card),
        areaStyle: areaStyle ? { color: palette.spendGradient } : undefined,
        data: spend,
        name: SPEND_LABEL,
        type: "line" as const,
        yAxisIndex: 1,
        z: 3,
      },
      // Helper series trail the data series so tests and tooltips can keep
      // treating indexes 0 and 1 as impressions and spend.
      ...bandSeries,
    ],
    tooltip: {
      ...buildTooltipStyle(isDark),
      formatter: (params: unknown) => {
        const items = (
          params as {
            seriesName: string;
            value: number;
            marker: string;
            name: string;
          }[]
        ).filter(({ seriesName }) => DATA_SERIES_LABELS.includes(seriesName));
        const header = items[0]?.name ?? "";
        const tooltipRows = items
          .map((item) => {
            const value =
              item.seriesName === SPEND_LABEL
                ? CURRENCY_FORMATTER.format(item.value)
                : IMPRESSION_FORMATTER.format(item.value);
            return `${item.marker}${item.seriesName}: <b style="${valueStyle}">${value}</b>`;
          })
          .join("<br/>");
        return `${header}<br/>${tooltipRows}`;
      },
      trigger: "axis",
    },
    xAxis: {
      axisLabel: { ...axisLabelStyle },
      axisLine: { lineStyle: { color: gridLineColor } },
      axisTick: { show: false },
      // Bars need half-category padding; area/line run edge to edge.
      boundaryGap: primaryType === "bar",
      data: dates,
      type: "category",
    },
    // Fixed max + identical splitNumber on both axes keeps their gridlines
    // coincident (no duplicate background lines) and makes the projected
    // band geometry exact.
    yAxis: [
      {
        axisLabel: { ...axisLabelStyle, formatter: formatImpressionAxis },
        max: impressionsMax,
        min: 0,
        splitLine: { lineStyle: { color: gridLineColor } },
        splitNumber: AXIS_SPLIT_NUMBER,
        type: "value",
      },
      {
        axisLabel: { ...axisLabelStyle, formatter: formatSpendAxis },
        max: spendMax,
        min: 0,
        position: "right",
        splitLine: { show: false },
        splitNumber: AXIS_SPLIT_NUMBER,
        type: "value",
      },
    ],
  };
};

export const buildPerformanceChartOption = (
  rows: PerformanceReportRow[],
  chartType: ChartType,
  isDark: boolean
): EChartsOption => {
  switch (chartType) {
    case "area":
      return buildTimeSeriesOption(rows, "line", true, isDark);
    case "bar":
      return buildTimeSeriesOption(rows, "bar", false, isDark);
    case "line":
      return buildTimeSeriesOption(rows, "line", false, isDark);
    default: {
      // Exhaustiveness check: if a ChartType is ever added without a case
      // above, `chartType` here is no longer `never` and this line fails to
      // compile — a silent bar-chart fallback would hide the missing case
      // instead of catching it at build time.
      const unhandledChartType: never = chartType;
      throw new Error(`Unhandled chart type: ${unhandledChartType}`);
    }
  }
};

export const PERFORMANCE_CHART_TYPE_OPTIONS: {
  key: ChartType;
  label: string;
}[] = [
  { key: "bar", label: TEXT.campaignOverview.chart.typeBar },
  { key: "line", label: TEXT.campaignOverview.chart.typeLine },
  { key: "area", label: TEXT.campaignOverview.chart.typeArea },
];
